import { Box, Button, Typography, Paper } from "@mui/material";
import { Link } from "@tanstack/react-router";

import type { ErrorComponentProps } from "@tanstack/react-router";

/**
 * Chunk load errors occur when dynamically imported route modules fail to fetch.
 * Common causes: deployment (old chunk hashes 404), network issues, browser cache.
 * Full page reload is the correct fix - it fetches fresh HTML and current chunk assets.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("importing a module script failed")
  );
}

// Session expiry is handled globally in AuthRouterProvider, off the report
// connectClient makes — not here, where only an errored loader would reach it.
export function ErrorComponent({ error, reset }: ErrorComponentProps) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "50vh",
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 5,
          maxWidth: 600,
          width: "100%",
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {isChunkLoadError(error)
            ? "The app updated. Please reload the page to get the latest version."
            : error instanceof Error
              ? error.message
              : "An unexpected error occurred"}
        </Typography>
        {!isChunkLoadError(error) && <Link to="/">Back to Dragon Tiles</Link>}
        <Button
          sx={{ ml: isChunkLoadError(error) ? 0 : 3 }}
          variant="contained"
          color="primary"
          onClick={() => {
            if (isChunkLoadError(error)) {
              window.location.reload();
            } else {
              reset();
            }
          }}
        >
          Try Again
        </Button>
      </Paper>
    </Box>
  );
}
